import { Injectable } from '@angular/core';
import { LiveChatStateService } from './live-chat-state.service';
import { AttendanceContentType, AttendanceMessage } from '../interfaces/attendance-list-history.interface';
import { AttendanceChannel } from '../interfaces/attendance';

export const INCOMMING_MD_MESSAGE_CHANNELS = [
  AttendanceChannel.WHATSAPP,
  AttendanceChannel.FACEBOOK,
  AttendanceChannel.GOOGLE_BUSINESS_MESSAGES,
  AttendanceChannel.BOOKING,
  AttendanceChannel.TELEGRAM
]

export const OUTGOING_MD_MESSAGE_CHANNELS = [
  AttendanceChannel.WHATSAPP,
  AttendanceChannel.FACEBOOK,
  AttendanceChannel.INSTAGRAM,
  AttendanceChannel.GOOGLE_BUSINESS_MESSAGES,
]

@Injectable({
  providedIn: 'root',
})
export class ChannelMessageTextParserService {
  private get channel(){
    return this.livechatState.currentAttendance?.currentContactChannel.channel;
  }

  constructor(private livechatState: LiveChatStateService) {}

  public adaptOutgoingMessages(message: string): string {
    const isTiktok = this.channel === AttendanceChannel.TIKTOK;
    const isBooking = this.channel === AttendanceChannel.BOOKING;
    const isExpedia = this.channel === AttendanceChannel.EXPEDIA;
    const isTelegram = this.channel === AttendanceChannel.TELEGRAM;

    if (isBooking) {
      return this.removeBackSlashes(
        this.toBookingPlainText(message)
      );
    }

    if (isExpedia) {
      let treatedMessage = this.toBookingPlainText(message);
      treatedMessage = this.removeBackSlashes(treatedMessage);
      treatedMessage = treatedMessage.replace(/\n/g, ' ');
      return treatedMessage;
    }

    if (isTiktok || isTelegram) {
      let treatedMessage = this.toBookingPlainText(message);
      treatedMessage = this.removeBackSlashes(treatedMessage);
      return treatedMessage;
    }

    if (OUTGOING_MD_MESSAGE_CHANNELS.includes(this.channel!)) {
      message = this.toWhatsappMarkdown(message)
      message = this.removeBackSlashes(message);
      return message
    } else {
      message = this.prependHttpToLinks(message);
      return message;
    }
  }

  public adaptIncomingMessages(message: AttendanceMessage){
    if(!INCOMMING_MD_MESSAGE_CHANNELS.includes(this.channel!)) return;

    const content = message?.data?.content;
    if (!content) return;


    switch (message.data.type) {
      case AttendanceContentType.TEXT:
          if(content.text)
            content.text = this.toHTML(content.text);
        break;
      case AttendanceContentType.IMAGE:
      case AttendanceContentType.VIDEO:
        if(content.media?.caption)
          content.media!.caption = this.toHTML(content.media.caption);
        if(content.media?.mediaText)
          content.media!.mediaText = this.toHTML(content.media.mediaText);
        break;
      case AttendanceContentType.DOCUMENT:
        if(content.media?.mediaText)
          content.media!.mediaText = this.toHTML(content.media.mediaText);
        break;
      case AttendanceContentType.LIST_BUTTONS:
        if(content.text) {
          content.text = this.toHTML(content.text);
        }
        break;
      default:
        break;
    }
  }

  private removeAnchor(text: string) {
    return text.replace(new RegExp('</?a.+?>', 'g'), '');
  }

  private removeBackSlashes(text: string) {
    return text.replace(new RegExp('\\\\', 'g'), '');
  }

  private prependHttpToLinks(myMessage: string): string {
    const regex = new RegExp('href="(?!http(?:s?)://)(.+?)"', 'g');
    let link = null;
    while ((link = regex.exec(myMessage))) {
      const replaced = link[0].replace(link[1], 'http://' + link[1]);
      myMessage = myMessage.replace(link[0], replaced);
    }
    return myMessage;
  }


  private toWhatsappMarkdown(htmlString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    function convertListToMarkdown(list: HTMLElement) {
      const items = list.querySelectorAll('li');
      return Array.from(items)
        .map((item, index) => {
          const prefix =
            list.tagName.toLowerCase() === 'ol' ? `${index + 1}. ` : '- ';
          return `${prefix}${processNode(item)}\n`;
        })
        .join('');
    }

    const isEmojiSpan = (n: ChildNode | null) =>
      n?.nodeType === Node.ELEMENT_NODE &&
      ((n as Element).classList.contains('emoji-text-bubble') ||
       (n as Element).classList.contains('quill-emoji'));

    function processNode(node: Node, parentChar = '') {
      if (node.nodeType === Node.TEXT_NODE) {
        let markdown = node.nodeValue;

        if(node.parentElement?.tagName.toLowerCase() === 'p' &&
        node.parentElement.parentElement?.tagName.toLowerCase() === 'body'
        ){
          const prevSiblingEndsWithSpace =
            node.previousSibling?.nodeType === Node.ELEMENT_NODE &&
            (node.previousSibling as Element).textContent?.endsWith(' ');

          const nextSiblingStartsWithSpace =
            node.nextSibling?.nodeType === Node.ELEMENT_NODE &&
            (node.nextSibling as Element).textContent?.startsWith(' ');

          if (
            !(markdown?.startsWith(' ') ||
            markdown?.endsWith(' ') ||
            /^[^\w\s]/.test(markdown ?? '') ||
            /[^\w\s]$/.test(markdown ?? '') ||
            prevSiblingEndsWithSpace ||
            nextSiblingStartsWithSpace) &&
            ((node.nextSibling?.nodeType === Node.ELEMENT_NODE &&
              !isEmojiSpan(node.nextSibling)) ||
             (node.previousSibling?.nodeType === Node.ELEMENT_NODE &&
              !isEmojiSpan(node.previousSibling)))
          )
            markdown = `\`\`\`${markdown}\`\`\``;
        }else if (node.nextSibling && !isEmojiSpan(node.nextSibling))
          markdown = `${markdown}${parentChar}${parentChar}`;

        return markdown;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        let char = '';
        let markdown = '';
        switch ((node as HTMLElement).tagName.toLowerCase()) {
          case 'p':
            markdown = `${Array.from(node.childNodes)
              .map((n) => processNode(n))
              .join('')}`;
            markdown = `${markdown}\n`;
            break;
          case 'strong':
          case 'b': {
            char = '*';
            const innerStrong = Array.from(node.childNodes).map((n) => processNode(n, char)).join('');
            const leadingStrong = innerStrong.match(/^\s+/)?.[0] ?? '';
            const trailingStrong = innerStrong.match(/\s+$/)?.[0] ?? '';
            markdown = `${markdown}${leadingStrong}${char}${innerStrong.trim()}${char}${trailingStrong}`;
            break;
          }
          case 'em':
          case 'i': {
            char = '_';
            const innerEm = Array.from(node.childNodes).map((n) => processNode(n, char)).join('');
            const leadingEm = innerEm.match(/^\s+/)?.[0] ?? '';
            const trailingEm = innerEm.match(/\s+$/)?.[0] ?? '';
            markdown = `${markdown}${leadingEm}${char}${innerEm.trim()}${char}${trailingEm}`;
            break;
          }
          case 'del':
          case 's': {
            char = '~';
            const innerDel = Array.from(node.childNodes).map((n) => processNode(n, char)).join('');
            const leadingDel = innerDel.match(/^\s+/)?.[0] ?? '';
            const trailingDel = innerDel.match(/\s+$/)?.[0] ?? '';
            markdown = `${markdown}${leadingDel}${char}${innerDel.trim()}${char}${trailingDel}`;
            break;
          }
          case 'li':
            markdown = `${Array.from(node.childNodes)
              .map((n) => processNode(n))
              .join('')}`;
            break;
          case 'code': {
            char = '`';
            const innerCode = Array.from(node.childNodes).map((n) => processNode(n, char)).join('');
            const leadingCode = innerCode.match(/^\s+/)?.[0] ?? '';
            const trailingCode = innerCode.match(/\s+$/)?.[0] ?? '';
            markdown = `${markdown}${leadingCode}${char}${innerCode.trim()}${char}${trailingCode}`;
            break;
          }
          case 'ul':
          case 'ol':
            markdown = convertListToMarkdown(node as HTMLElement);
            if (node.nextSibling) markdown = `${markdown}`;
            break;
          default:
            markdown = Array.from(node.childNodes)
              .map((n) => processNode(n))
              .join('');
        }
        return markdown;
      }
      return '';
    }
    return Array.from(doc.childNodes)
      .map((n) => processNode(n))
      .join('');
  }

  private toBookingPlainText(htmlString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    function convertListToPlainList(list: HTMLElement) {
      const items = list.querySelectorAll('li');
      return Array.from(items)
        .map((item, index) => {
          const prefix =
            list.tagName.toLowerCase() === 'ol' ? `${index + 1}. ` : '- ';
          return `${prefix}${processNode(item)}\n`;
        })
        .join('');
    }
    function processNode(node: Node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        let markdown = '';
        switch ((node as HTMLElement).tagName.toLowerCase()) {
          case 'p':
            markdown = `${Array.from(node.childNodes)
              .map((n) => processNode(n))
              .join('')}`;
            markdown = `${markdown}\n`;
            break;
          case 'li':
            markdown = `${Array.from(node.childNodes)
              .map((n) => processNode(n))
              .join('')}`;
            break;
          case 'ul':
          case 'ol':
            markdown = convertListToPlainList(node as HTMLElement);
            if (node.nextSibling) markdown = `${markdown}`;
            break;
          default:
            markdown = Array.from(node.childNodes)
              .map((n) => processNode(n))
              .join('');
        }
        return markdown;
      }
      return '';
    }
    return Array.from(doc.childNodes)
      .map((n) => processNode(n))
      .join('');
  }

  private toHTML(whatsappMarkdown: string) {
    return whatsappMarkdown
      .replace(/```([\s\S]*?)```/g, '<span class="markdown-text">$1</span>')
      .replace(/(?<![\p{L}\p{M}\p{N}])(?:\*)(?:(?!\s))((?:(?!\*|\n).)+)(?:\*)(?![\p{L}\p{M}\p{N}])/gu, '<strong>$1</strong>')
      .replace(/(?<![\p{L}\p{M}\p{N}])(?:_)(?:(?!\s))((?:(?!\n|_).)+)(?:_)(?![\p{L}\p{M}\p{N}])/gu, '<i>$1</i>')
      .replace(/(?<![\p{L}\p{M}\p{N}])(?:~)(?:(?!\s))((?:(?!\n|~).)+)(?:~)(?![\p{L}\p{M}\p{N}])/gu, '<s>$1</s>')
      .replace(/(?<![\p{L}\p{M}\p{N}])(?:--)(?:(?!\s))((?:(?!\n|--).)+)(?:--)(?![\p{L}\p{M}\p{N}])/gu, '<u>$1</u>')
      .replace(/(?<![\p{L}\p{M}\p{N}])(?:`)(?:(?!\s))((?:(?!\n|`).)+)(?:`)(?![\p{L}\p{M}\p{N}])/gu, '<code>$1</code>')
      .replace(/^-/gm, '•');
  }
}
